import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { LocalDataSource } from 'ng2-smart-table';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { ListParams } from '../../../common/interfaces/base-crud.interface';
import { ErrorHandlerService } from '../../../common/services/error-handler.service';
import { formatJalali } from '../../../common/utils/date';
import { convertToEnglishDigits, formatToman } from '../../../common/utils/string';
import { User } from '../../users/models/user.model';
import { UsersService } from '../../users/users.service';
import { CheckoutAcceptModalComponent } from '../checkout-accept-modal/checkout-accept-modal.component';
import { CheckoutItem, CheckoutService } from '../checkout.service';
import { CheckoutListCellComponent } from './checkout-list-cell.component';

@Component({
  selector: 'ngx-checkout-list',
  templateUrl: './checkout-list.component.html',
  styleUrls: ['./checkout-list.component.scss'],
})
export class CheckoutListComponent implements OnInit, OnDestroy {
  source = new LocalDataSource();
  loading = false;
  errorMessage = '';
  currentPage = 1;
  perPage = 10;
  total = 0;
  filter: NonNullable<ListParams<CheckoutItem>['filter']> = {};
  sort: NonNullable<ListParams<CheckoutItem>['sort']> = { createdAt: 'DESC' };
  private readonly requesterById = new Map<string, User>();
  private readonly destroy$ = new Subject<void>();
  private listSubscription?: Subscription;

  settings: any = {
    mode: 'external',
    actions: false,
    pager: { display: false, perPage: 10 },
    noDataMessage: 'درخواستی برای نمایش وجود ندارد.',
    columns: {
      user: {
        title: 'درخواست‌دهنده',
        type: 'custom',
        renderComponent: CheckoutListCellComponent,
        filter: true,
        filterFunction: () => true,
        valuePrepareFunction: (_: any, item: CheckoutItem) => ({
          type: 'requester',
          name: this.getRequesterName(item),
          contact: this.getRequesterContact(item),
        }),
      },
      amount: {
        title: 'مبلغ',
        type: 'number',
        filter: false,
        valuePrepareFunction: (value: number) => formatToman(value || 0),
      },
      cardNumber: {
        title: 'شماره کارت',
        type: 'string',
        filter: true,
        filterFunction: () => true,
        valuePrepareFunction: (_: any, item: CheckoutItem) => this.getCardNumber(item),
      },
      sheba: {
        title: 'شماره شبا',
        type: 'string',
        filter: true,
        filterFunction: () => true,
        valuePrepareFunction: (_: any, item: CheckoutItem) => this.getSheba(item),
      },
      description: {
        title: 'توضیحات',
        type: 'string',
        filter: true,
        filterFunction: () => true,
        valuePrepareFunction: (value: string) => value || 'بدون توضیحات',
      },
      status: {
        title: 'وضعیت',
        type: 'custom',
        renderComponent: CheckoutListCellComponent,
        filter: false,
        valuePrepareFunction: (value: string) => ({
          type: 'status',
          label: this.getStatusLabel(value),
          className: this.getStatusClass(value),
        }),
      },
      transaction: {
        title: 'کد پیگیری',
        type: 'string',
        filter: true,
        filterFunction: () => true,
        valuePrepareFunction: (_: any, item: CheckoutItem) =>
          item.transaction || item.trackingCode || '-',
      },
      createdAt: {
        title: 'تاریخ ثبت',
        type: 'string',
        filter: false,
        valuePrepareFunction: (value: string | Date) => this.formatDate(value),
      },
      action: {
        title: 'عملیات',
        type: 'custom',
        renderComponent: CheckoutListCellComponent,
        filter: false,
        sort: false,
        valuePrepareFunction: (_: any, item: CheckoutItem) => ({
          type: 'action',
          canAccept: this.canAccept(item),
        }),
        onComponentInitFunction: (instance: CheckoutListCellComponent) => {
          instance.accept.pipe(takeUntil(this.destroy$)).subscribe((item) => {
            this.openAcceptModal(item);
          });
        },
      },
    },
  };

  constructor(
    private readonly checkoutService: CheckoutService,
    private readonly dialogService: NbDialogService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly usersService: UsersService,
  ) {}

  ngOnInit(): void {
    this.source.onChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe((change) => {
        if (change.action === 'filter') {
          this.filter = {};
          for (const item of change.filter?.filters || []) {
            let value = convertToEnglishDigits(String(item.search || '')).trim();
            if (item.field === 'cardNumber' || item.field === 'sheba') {
              value = value.replace(/[\s-]/g, '');
            }
            if (item.field === 'sheba') value = value.toUpperCase();
            const field = item.field === 'user' ? 'user.name' : item.field;
            if (value) this.filter[field] = value;
          }
          this.currentPage = 1;
          this.loadItems();
        }

        if (change.action === 'sort') {
          this.sort = {};
          for (const item of change.sort || []) {
            const field = item.field === 'user' ? 'user.name' : item.field;
            this.sort[field] = String(item.direction).toLowerCase() === 'desc' ? 'DESC' : 'ASC';
          }
          if (!Object.keys(this.sort).length) this.sort = { createdAt: 'DESC' };
          this.currentPage = 1;
          this.loadItems();
        }
      });

    this.loadItems();
  }

  get hasFilters(): boolean {
    return Object.keys(this.filter).length > 0;
  }

  clearSearch(): void {
    this.source.setFilter([]);
  }

  loadItems(): void {
    this.listSubscription?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';

    this.listSubscription = this.checkoutService
      .getCheckoutRequests(this.currentPage, this.perPage, {
        filter: this.filter,
        sort: this.sort,
      })
      .pipe(
        switchMap((res) => this.resolveRequesters(res)),
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          const items = res?.items || [];
          this.total = res?.total ?? items.length;
          this.source.load(items);
        },
        error: (error) => {
          this.total = 0;
          this.source.load([]);
          this.errorMessage =
            error?.error?.persianMessage ||
            error?.error?.message ||
            error?.message ||
            'خطا در دریافت درخواست‌های برداشت';
          this.errorHandler.handle(error);
        },
      });
  }

  handleChangePage(page: number): void {
    this.currentPage = page;
    this.loadItems();
  }

  getId(item: CheckoutItem): string {
    return item?.id || item?._id || '';
  }

  getRequesterName(item: CheckoutItem): string {
    const entity = this.getRequester(item);

    if (!entity) return '-';

    const fullName =
      entity?.fullName ||
      entity?.fullname ||
      [entity?.name || entity?.firstName, entity?.lastName]
        .filter(Boolean)
        .join(' ');

    return (
      fullName ||
      entity?.mobile ||
      entity?.phone ||
      entity?.email ||
      entity?.id ||
      entity?._id ||
      '-'
    );
  }

  getRequesterContact(item: CheckoutItem): string {
    const entity = this.getRequester(item);

    if (!entity) return '';

    return entity?.mobile || entity?.phone || entity?.email || '';
  }

  private getRequester(item: CheckoutItem): any {
    const entity =
      item?.user ||
      item?.requester ||
      item?.operator?.user ||
      item?.operator ||
      item?.agent?.user ||
      item?.agent;

    if (typeof entity === 'string') {
      return this.requesterById.get(entity) || null;
    }

    return entity || null;
  }

  getCardNumber(item: CheckoutItem): string {
    return (
      item?.cardNumber ||
      item?.card?.cardNumber ||
      item?.bankCard?.cardNumber ||
      '-'
    );
  }

  getSheba(item: CheckoutItem): string {
    return item?.sheba || item?.card?.sheba || item?.bankCard?.sheba || '-';
  }

  getStatusLabel(status?: string): string {
    const normalized = String(status || 'PENDING').toUpperCase();
    const labels: { [key: string]: string } = {
      PENDING: 'در انتظار بررسی',
      REQUESTED: 'در انتظار بررسی',
      ACCEPTED: 'تأیید شده',
      APPROVED: 'تأیید شده',
      PAID: 'پرداخت شده',
      COMPLETED: 'تکمیل شده',
      DONE: 'تکمیل شده',
      SUCCESS: 'موفق',
      SUCCESSFUL: 'موفق',
      REJECTED: 'رد شده',
      FAILED: 'ناموفق',
      CANCELLED: 'لغو شده',
      CANCELED: 'لغو شده',
    };

    return labels[normalized] || status || '-';
  }

  getStatusType(status?: string): string {
    const normalized = String(status || 'PENDING').toUpperCase();

    if (
      [
        'ACCEPTED',
        'APPROVED',
        'PAID',
        'COMPLETED',
        'DONE',
        'SUCCESS',
        'SUCCESSFUL',
      ].includes(normalized)
    ) {
      return 'success';
    }

    if (['REJECTED', 'FAILED', 'CANCELLED', 'CANCELED'].includes(normalized)) {
      return 'danger';
    }

    return 'warning';
  }

  getStatusClass(status?: string): string {
    return `status-badge status-badge--${this.getStatusType(status)}`;
  }

  canAccept(item: CheckoutItem): boolean {
    if (!this.getId(item)) return false;

    const status = String(item?.status || 'PENDING').toUpperCase();
    return ![
      'ACCEPTED',
      'APPROVED',
      'PAID',
      'COMPLETED',
      'DONE',
      'SUCCESS',
      'SUCCESSFUL',
      'REJECTED',
      'FAILED',
      'CANCELLED',
      'CANCELED',
    ].includes(status);
  }

  formatDate(value?: string | Date): string {
    return value ? formatJalali(value, true) || '-' : '-';
  }

  openAcceptModal(item: CheckoutItem): void {
    const id = this.getId(item);

    if (!id || !this.canAccept(item)) return;

    this.dialogService
      .open(CheckoutAcceptModalComponent, {
        context: {
          checkoutId: id,
          checkout: item,
        },
        closeOnBackdropClick: false,
        dialogClass: 'checkout-accept-dialog',
      })
      .onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result) {
          this.loadItems();
        }
      });
  }

  private resolveRequesters(res: any) {
    const items = (res?.items || []) as CheckoutItem[];
    const unresolvedIds = Array.from(
      new Set(
        items
          .map((item) => (typeof item?.user === 'string' ? item.user : ''))
          .filter((id) => !!id && !this.requesterById.has(id)),
      ),
    );

    if (!unresolvedIds.length) {
      return of(this.attachResolvedRequesters(res));
    }

    return forkJoin(
      unresolvedIds.map((id) =>
        this.usersService.getById(id).pipe(catchError(() => of(null))),
      ),
    ).pipe(
      map((responses) => {
        responses.forEach((response: any, index) => {
          const user = response?.data as User | undefined;
          if (user) {
            this.requesterById.set(unresolvedIds[index], user);
          }
        });

        return this.attachResolvedRequesters(res);
      }),
    );
  }

  private attachResolvedRequesters(res: any): any {
    return {
      ...res,
      items: (res?.items || []).map((item: CheckoutItem) => {
        if (typeof item?.user !== 'string') return item;

        const user = this.requesterById.get(item.user);
        return user ? { ...item, user } : item;
      }),
    };
  }

  ngOnDestroy(): void {
    this.listSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
